import type { AnalyticsContext } from "./envelope";
import { EVENTS } from "./registry";
import type { Track, TrackResult } from "./track";

/**
 * Typed funnel-stage event helpers (M0.md "Events plumbed (props filled
 * later)"). Later milestones only fill in props — the event name and
 * envelope plumbing already exist here. `map_rendered_first` is the
 * north-star activation event; `time_to_first_map_ms` is required so it's
 * never emitted without the metric that makes it useful.
 *
 * Event names come from the code-owned registry (`EVENTS`) — call sites and
 * this factory never hardcode strings, so the CI registry test catches drift.
 */

export function createFunnelEvents(track: Track) {
  return {
    appOpened: (
      ctx: AnalyticsContext,
      props: { referrer?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string } = {},
    ): TrackResult => track(EVENTS.APP_OPENED, props, ctx),

    intakeViewed: (ctx: AnalyticsContext, props: { intake_surface: "homepage" | "new_map" }): TrackResult =>
      track(EVENTS.INTAKE_VIEWED, props, ctx),

    intakeMethodOpened: (
      ctx: AnalyticsContext,
      props: { intake_surface: "homepage" | "new_map"; method: "paste" | "sheets" },
    ): TrackResult => track(EVENTS.INTAKE_METHOD_OPENED, props, ctx),

    intakeFilePickerOpened: (
      ctx: AnalyticsContext,
      props: { intake_surface: "homepage" | "new_map" },
    ): TrackResult => track(EVENTS.INTAKE_FILE_PICKER_OPENED, props, ctx),

    sheetFetchStarted: (
      ctx: AnalyticsContext,
      props: { intake_surface: "homepage" | "new_map" },
    ): TrackResult => track(EVENTS.SHEET_FETCH_STARTED, props, ctx),

    sheetFetchSucceeded: (
      ctx: AnalyticsContext,
      props: { intake_surface: "homepage" | "new_map"; row_count: number },
    ): TrackResult => track(EVENTS.SHEET_FETCH_SUCCEEDED, props, ctx),

    sheetFetchFailed: (
      ctx: AnalyticsContext,
      props: { intake_surface: "homepage" | "new_map"; failure_code: string },
    ): TrackResult => track(EVENTS.SHEET_FETCH_FAILED, props, ctx),

    importStarted: (
      ctx: AnalyticsContext,
      props: { source: "paste" | "upload" | "sheets"; intake_surface: "homepage" | "new_map" },
    ): TrackResult => track(EVENTS.IMPORT_STARTED, props, ctx),

    columnMappingProposed: (
      ctx: AnalyticsContext,
      props: { column_count: number; address_column_count: number },
    ): TrackResult => track(EVENTS.COLUMN_MAPPING_PROPOSED, props, ctx),

    /** User overrode the auto-detected mapping — the inverse health signal for auto-detect. */
    columnMappingCorrected: (
      ctx: AnalyticsContext,
      props: { columns_corrected_count: number },
    ): TrackResult => track(EVENTS.COLUMN_MAPPING_CORRECTED, props, ctx),

    importCommitted: (
      ctx: AnalyticsContext,
      props: {
        dataset_id: string;
        row_count: number;
        source: "paste" | "upload" | "sheets";
        intake_surface: "homepage" | "new_map";
      },
    ): TrackResult => track(EVENTS.IMPORT_COMMITTED, props, ctx),

    /** Import errored before commit — drop-off diagnosis at the top of the funnel. */
    importFailed: (
      ctx: AnalyticsContext,
      props: {
        failure_code: string;
        source_kind?: string;
        source: "paste" | "upload" | "sheets";
        intake_surface: "homepage" | "new_map";
      },
    ): TrackResult => track(EVENTS.IMPORT_FAILED, props, ctx),

    /**
     * North-star activation event — the literal measurement of the 30-second
     * promise. Carries the FULL §6 required shape (`time_to_first_map_ms`,
     * `marker_count`, `style`, `geocode_strategy`); `map_id` rides along when a
     * map identity exists. Fired once per identity at the REAL first-paint moment
     * (the editor's `presentMap`), never on component mount with a placeholder.
     */
    mapRenderedFirst: (
      ctx: AnalyticsContext,
      props: {
        time_to_first_map_ms: number;
        marker_count: number;
        style: string;
        geocode_strategy: string;
        map_id?: string;
      },
    ): TrackResult => track(EVENTS.MAP_RENDERED_FIRST, props, ctx),

    // M5 NL Mapping & Q&A funnel (M5.md §Analytics, consumed by the M12 funnel).
    nlQuerySubmitted: (
      ctx: AnalyticsContext,
      props: { intent_hint?: string; prompt_len: number },
    ): TrackResult => track(EVENTS.NL_QUERY_SUBMITTED, props, ctx),

    /** The aha moment — fires once per identity; carries the classified intent + latency-from-first-map. */
    nlQueryFirst: (
      ctx: AnalyticsContext,
      props: { intent: string; time_since_first_map_ms: number },
    ): TrackResult => track(EVENTS.NL_QUERY_FIRST, props, ctx),

    nlQuerySucceeded: (
      ctx: AnalyticsContext,
      props: { intent: string; confidence: number; op_count: number; had_fallback: boolean },
    ): TrackResult => track(EVENTS.NL_QUERY_SUCCEEDED, props, ctx),

    nlQueryFailed: (
      ctx: AnalyticsContext,
      props: { failure_code: string; intent: string },
    ): TrackResult => track(EVENTS.NL_QUERY_FAILED, props, ctx),

    nlAnswerHighlighted: (
      ctx: AnalyticsContext,
      props: { highlighted_row_count: number },
    ): TrackResult => track(EVENTS.NL_ANSWER_HIGHLIGHTED, props, ctx),

    /** Undo-of-an-NL-op signal — the misinterpretation-rate metric's data source (quick-undo when within_10s). */
    nlQueryUndone: (
      ctx: AnalyticsContext,
      props: { within_10s: boolean; ms_since_apply: number },
    ): TrackResult => track(EVENTS.NL_QUERY_UNDONE, props, ctx),

    /** Style/filter mutations carry `via` so NL-driven edits are separable from GUI edits. */
    mapStyled: (
      ctx: AnalyticsContext,
      props: { via: "gui" | "nl"; style?: string; column?: string },
    ): TrackResult => track(EVENTS.MAP_STYLED, props, ctx),

    mapFiltered: (
      ctx: AnalyticsContext,
      props: { via: "gui" | "nl"; filter_field?: string },
    ): TrackResult => track(EVENTS.MAP_FILTERED, props, ctx),

    mapSaveStarted: (
      ctx: AnalyticsContext,
      props: { save_attempt_id: string; save_source: string; is_first_save: boolean },
    ): TrackResult => track(EVENTS.MAP_SAVE_STARTED, props, ctx),

    mapSaved: (
      ctx: AnalyticsContext,
      props: {
        map_id: string;
        is_first_save?: boolean;
        saved_map_count?: number;
        save_attempt_id?: string;
        save_source?: string;
      },
    ): TrackResult => track(EVENTS.MAP_SAVED, props, ctx),

    mapSaveFailed: (
      ctx: AnalyticsContext,
      props: {
        save_attempt_id: string;
        save_source: string;
        failure_code: string;
        is_first_save: boolean;
        http_status?: number;
      },
    ): TrackResult => track(EVENTS.MAP_SAVE_FAILED, props, ctx),

    /** Week-N retention cohorts — an owner returned to a saved map. */
    mapReturned: (
      ctx: AnalyticsContext,
      props: { days_since_last: number; saved_map_count?: number },
    ): TrackResult => track(EVENTS.MAP_RETURNED, props, ctx),

    embedSnippetCopied: (
      ctx: AnalyticsContext,
      props: { map_id: string; embed_mode: string },
    ): TrackResult => track(EVENTS.EMBED_SNIPPET_COPIED, props, ctx),

    // M6 Share & Embed growth/retention events (contracts §6.2 Stage D).
    shareCreated: (
      ctx: AnalyticsContext,
      props: { map_id: string; visibility: string; embed_mode: string; allow_nl: boolean },
    ): TrackResult => track(EVENTS.SHARE_CREATED, props, ctx),

    publicMapViewed: (ctx: AnalyticsContext, props: { share_slug: string; referrer_host?: string }): TrackResult =>
      track(EVENTS.PUBLIC_MAP_VIEWED, props, ctx),

    embedViewed: (
      ctx: AnalyticsContext,
      props: { share_slug: string; referrer_host?: string; source_surface: "embed" },
    ): TrackResult => track(EVENTS.EMBED_VIEWED, props, ctx),

    attributionClicked: (ctx: AnalyticsContext, props: { share_slug: string }): TrackResult =>
      track(EVENTS.ATTRIBUTION_CLICKED, props, ctx),

    datasetRefreshed: (ctx: AnalyticsContext, props: { map_id: string }): TrackResult =>
      track(EVENTS.DATASET_REFRESHED, props, ctx),

    // NOTE: the monetization events (`paywall_hit`, `checkout_completed`,
    // `subscription_canceled`) are DELIBERATELY not on this funnel factory.
    // `paywall_hit` is emitted through the canonical `emitPaywallHit`
    // (@/lib/analytics/paywall) with the §6 required `{trigger, quota_type,
    // usage}` shape, and the billing-truthed `checkout_completed` /
    // `subscription_canceled` are derived server-side by the reconciler
    // (@/lib/analytics/reconciler) with `plan_code`. Earlier helpers here sent
    // the wrong keys (`plan_id`, `{action, limit}`) and were superseded — they
    // were removed so no client path can re-introduce the drift.
  };
}

export type FunnelEvents = ReturnType<typeof createFunnelEvents>;
